import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, Heart, ArrowRight, Users, ChevronRight, UserPlus, Check, X, Activity, MessageCircle, ClipboardCheck, Target, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const ACTIVITY_ICON = {
  kpi_session: ClipboardCheck,
  round_review: Activity,
  note_reply: MessageCircle,
};

const ACTIVITY_TINT = {
  kpi_session: '#2C5530',
  round_review: '#1A2E1D',
  note_reply: '#BC562B',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const ActivityRow = ({ item }) => {
  const Icon = ACTIVITY_ICON[item.type] || Activity;
  const tint = ACTIVITY_TINT[item.type] || '#1A2E1D';
  const detail = (() => {
    if (item.type === 'kpi_session') return `${item.kpi ? item.kpi.charAt(0).toUpperCase() + item.kpi.slice(1) : 'Round'} · ${item.score ?? '—'}/10`;
    if (item.type === 'round_review') {
      const gap = item.gap_aa;
      const gapStr = gap == null ? '' : ` · Gap ${gap > 0 ? `+${gap}` : gap}`;
      return `${item.course_name || 'Round'}${gapStr}`;
    }
    if (item.type === 'note_reply') return item.preview || '';
    return '';
  })();

  const toHref = (() => {
    if (item.type === 'note_reply' && item.round_review_id) return `/coach/locker/${item.player_id}`;
    return `/coach/locker/${item.player_id}`;
  })();

  return (
    <li data-testid={`activity-${item.type}-${item.ref_id}`}>
      <Link to={toHref} className="flex items-start gap-3 p-4 hover:bg-[#FAFAF9] transition-colors border-b border-[#F2F0E9] last:border-b-0">
        <span
          className="w-8 h-8 flex items-center justify-center text-white flex-shrink-0 mt-0.5"
          style={{ backgroundColor: tint }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[#1A1A1A]">
            <span className="font-semibold">{item.player_name || 'Player'}</span>{' '}
            <span className="text-[#57534E]">{item.label}</span>
          </p>
          {detail && <p className="text-xs text-[#57534E] mt-0.5 truncate">{detail}</p>}
        </div>
        <span className="text-[11px] text-[#A8A29E] flex-shrink-0 whitespace-nowrap">{timeAgo(item.created_at)}</span>
      </Link>
    </li>
  );
};

const CoachLockerStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState({ at_risk_count: 0, threshold: 30, students: [] });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const [s, r, a, al] = await Promise.all([
        API.get('/coach/locker/students'),
        API.get('/coach/locker/requests'),
        API.get('/coach/locker/activity').catch(() => ({ data: [] })),
        API.get('/coach/locker/focus-alerts').catch(() => ({ data: { at_risk_count: 0, threshold: 30, students: [] } })),
      ]);
      setStudents(s.data);
      setRequests(r.data);
      setActivity(a.data || []);
      setAlerts(al.data || { at_risk_count: 0, threshold: 30, students: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolveRequest = async (id, approve) => {
    setActing(id);
    try {
      await API.post(`/coach/locker/requests/${id}/${approve ? 'approve' : 'reject'}`);
      toast.success(approve ? 'Player linked.' : 'Request declined.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="coach-locker-students">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-5xl flex items-center justify-between">
          <Link to="/coach" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-coach">
            <ArrowLeft className="w-4 h-4" /> Coach
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-5xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            <Users className="w-4 h-4" /> Coach view · Player's Locker
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">Your players</h1>
          <p className="text-[#57534E] max-w-2xl">Read-only view of every linked student's Awareness, Acceptance & Action — plus their Gap.</p>
        </div>

        {/* Focus adherence alert banner */}
        {alerts.at_risk_count > 0 && (
          <div className="mb-8 bg-[#FBEAEA] border-l-4 border-[#8B1E1E] px-5 py-4" data-testid="focus-alert-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#8B1E1E] mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#8B1E1E] text-sm">
                  {alerts.at_risk_count} player{alerts.at_risk_count === 1 ? ' needs' : 's need'} your attention
                </p>
                <p className="text-xs text-[#57534E] mt-0.5">
                  Below {alerts.threshold}% focus-prompt adherence this week:
                </p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {alerts.students.slice(0, 6).map((a) => (
                    <li key={a.player_id}>
                      <a
                        href={`/coach/locker/${a.player_id}`}
                        className="inline-flex items-center gap-1.5 bg-white border border-[#8B1E1E]/30 hover:border-[#8B1E1E] text-[#8B1E1E] text-xs font-semibold px-2.5 py-1"
                        data-testid={`focus-alert-${a.player_id}`}
                        title={`${a.done_last_7}/${a.total_slots} check-offs over 7 days (${a.percent_last_7}%)`}
                      >
                        <Target className="w-3 h-3" /> {a.player_name}
                        <span className="opacity-70">· {a.percent_last_7}%</span>
                      </a>
                    </li>
                  ))}
                  {alerts.students.length > 6 && (
                    <li className="text-xs text-[#57534E] self-center">+{alerts.students.length - 6} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Pending coach-link requests */}
            {requests.length > 0 && (
              <section className="mb-10" data-testid="pending-requests-section">
                <div className="flex items-center gap-2 mb-4">
                  <UserPlus className="w-4 h-4 text-[#BC562B]" />
                  <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Pending requests · {requests.length}</h2>
                </div>
                <ul className="space-y-3">
                  {requests.map((r) => (
                    <li key={r.id} className="bg-white border border-[#BC562B]/40 p-5" data-testid={`pending-request-${r.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#1A1A1A]">{r.player?.full_name || r.player?.email || 'Player'}</p>
                          <p className="text-xs text-[#737373] mt-0.5">{r.player?.email}{r.player?.handicap != null && ` · Handicap ${r.player.handicap}`}</p>
                          {r.message && (
                            <p className="text-sm text-[#57534E] mt-3 border-l-2 border-[#BC562B] pl-3 italic">"{r.message}"</p>
                          )}
                          <p className="text-[10px] text-[#A8A29E] mt-3 uppercase tracking-wider">Requested {new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => resolveRequest(r.id, false)}
                            disabled={acting === r.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] disabled:opacity-50"
                            data-testid={`reject-request-${r.id}`}
                          >
                            <X className="w-3.5 h-3.5" /> Decline
                          </button>
                          <button
                            onClick={() => resolveRequest(r.id, true)}
                            disabled={acting === r.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#2C5530] text-white hover:bg-[#1E3A21] disabled:opacity-50"
                            data-testid={`approve-request-${r.id}`}
                          >
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {students.length === 0 ? (
              <div className="text-center py-20 bg-white border border-[#E7E5E4]" data-testid="empty-students">
                <Users className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
                <p className="text-[#737373]">No players linked to you yet.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 items-start">
                {/* Students list */}
                <ul className="space-y-3">
                  {students.map((s) => (
                    <li key={s.id} className="bg-white border border-[#E7E5E4] hover:border-[#2C5530] transition-colors" data-testid={`coach-student-row-${s.id}`}>
                      <Link to={`/coach/locker/${s.id}`} className="flex items-center justify-between p-5 gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <span className="font-semibold text-[#1A1A1A]">{s.full_name || 'Unnamed'}</span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#F2F0E9] text-[#57534E] px-2 py-0.5">{s.subscription_tier || 'free'}</span>
                            <span className="text-[10px] uppercase tracking-wider text-[#737373]">{s.round_reviews} round{s.round_reviews === 1 ? '' : 's'}</span>
                          </div>
                          <p className="text-xs text-[#737373] mb-2">{s.email}</p>
                          <div className="flex flex-wrap gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-[#2C5530] font-semibold"><Brain className="w-3.5 h-3.5" /> {s.latest_scores?.awareness ?? '—'}/10</span>
                            <span className="inline-flex items-center gap-1 text-[#BC562B] font-semibold"><Heart className="w-3.5 h-3.5" /> {s.latest_scores?.acceptance ?? '—'}/10</span>
                            <span className="inline-flex items-center gap-1 text-[#1A2E1D] font-semibold"><ArrowRight className="w-3.5 h-3.5" /> {s.latest_scores?.action ?? '—'}/10</span>
                            {s.latest_gap_aa != null && (
                              <span className="inline-flex items-center gap-1 text-[#737373] font-semibold border-l border-[#E7E5E4] pl-3">
                                Gap {s.latest_gap_aa > 0 ? `+${s.latest_gap_aa}` : s.latest_gap_aa}
                              </span>
                            )}
                            {s.focus && (() => {
                              const pct = s.focus.percent_last_7;
                              const tone = pct >= 70 ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                                : pct >= 30 ? 'bg-[#FFF4ED] text-[#BC562B] border-[#BC562B]/40'
                                : 'bg-[#FBEAEA] text-[#8B1E1E] border-[#8B1E1E]/30';
                              return (
                                <span
                                  className={`inline-flex items-center gap-1 font-semibold border px-2 py-0.5 ${tone}`}
                                  title={`${s.focus.done_last_7}/${s.focus.total_slots} focus check-offs in the last 7 days · ${s.focus.done_today}/${s.focus.active} done today`}
                                  data-testid={`focus-pill-${s.id}`}
                                >
                                  <Target className="w-3 h-3" /> {s.focus.done_last_7}/{s.focus.total_slots} wk
                                  {s.focus.done_today > 0 && <span className="opacity-70 ml-0.5">· {s.focus.done_today}/{s.focus.active} today</span>}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#A8A29E] flex-shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Activity feed */}
                <aside className="bg-white border border-[#E7E5E4] lg:sticky lg:top-6" data-testid="coach-activity-feed">
                  <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-[#F2F0E9]">
                    <Activity className="w-4 h-4 text-[#BC562B]" />
                    <h2 className="font-['Playfair_Display'] text-lg text-[#1A1A1A]">Recent activity</h2>
                  </div>
                  {activity.length === 0 ? (
                    <div className="px-5 py-8 text-center" data-testid="activity-empty">
                      <p className="text-sm text-[#A8A29E]">No recent activity from your players.</p>
                    </div>
                  ) : (
                    <ul className="max-h-[640px] overflow-y-auto">
                      {activity.map((item) => (
                        <ActivityRow key={`${item.type}-${item.ref_id}-${item.created_at}`} item={item} />
                      ))}
                    </ul>
                  )}
                </aside>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default CoachLockerStudentsPage;
